import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import ProductLocationPopUp from "../productPage/ProductLocationPopUp";

const getProduct = (row) => row.product || row;

const getFamilyName = (row) => {
  const hierarchy = row.categories?.[0]?.categoryHierarchy;
  if (!hierarchy?.length) return null;
  return hierarchy[0].isFamily ? hierarchy[0].slug : null;
};

const getCategoryValue = (row, levelFromEnd) => {
  const hierarchy = row.categories?.[0]?.categoryHierarchy;
  if (!hierarchy?.length || hierarchy.length < levelFromEnd) return "-";
  return hierarchy.at(-levelFromEnd)?.slug || "-";
};

export const EXSTRA_COLUMNS = [
  {
    key: "kosherSupervision[0].classification",
    label: "בשרי/חלבי",
    width: "0.9fr",
    getValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.classification || "-",
    getSortValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.classification || "",
  },
  {
    key: "weightJump",
    label: "קפיצת משקל",
    width: "0.8fr",
    getValue: (row) => getProduct(row).weightJump ?? "-",
    getSortValue: (row) => getProduct(row).weightJump ?? 0,
  },
  {
    key: "description",
    label: "תיאור",
    width: "1.2fr",
    getValue: (row) => getProduct(row).description || "-",
    getSortValue: (row) => getProduct(row).description || "",
  },
  {
    key: "kosherSupervision[0].supervisionAgency.name",
    label: "כשרות",
    width: "1fr",
    getValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.supervisionAgency?.name || "-",
    getSortValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.supervisionAgency?.name || "",
  },
  {
    key: "userCompany",
    label: "יצרן",
    width: "1fr",
    getValue: (row) => getProduct(row).userCompany || "-",
    getSortValue: (row) => getProduct(row).userCompany || "",
  },
  {
    key: "weightDetails.weighedProduct",
    label: "שקיל",
    width: "0.7fr",
    getValue: (row) =>
      getProduct(row).weightDetails?.weighedProduct ? "כן" : "לא",
    getSortValue: (row) =>
      getProduct(row).weightDetails?.weighedProduct ? 1 : 0,
  },
  {
    key: "kosherSupervision[0].kashrutFeature",
    label: "סוג כשרות",
    width: "1fr",
    getValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.kashrutFeature || "-",
    getSortValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.kashrutFeature || "",
  },
  {
    key: "vatExempt",
    label: "מע״מ",
    width: "0.7fr",
    getValue: (row) => (getProduct(row).vatExempt ? "פטור" : "חייב"),
    getSortValue: (row) => (getProduct(row).vatExempt ? 1 : 0),
  },
  {
    key: "showOutOfStock",
    label: "סימון מלאי",
    width: "0.8fr",
    getValue: (row) => (getProduct(row).showOutOfStock ? "מוצג" : "מוסתר"),
    getSortValue: (row) => (getProduct(row).showOutOfStock ? 1 : 0),
  },
];

export const ALL_COLUMNS = [
  {
    key: "gtin",
    label: "ברקוד",
    width: "1.3fr",
    base: true,
    getValue: (row) => getProduct(row).gtin || "-",
    getSortValue: (row) => getProduct(row).gtin || "",
  },
  {
    key: "name",
    label: "שם מוצר",
    width: "2.8fr",
    base: true,
    className: "product-name-cell",
    getValue: (row) => {
      const p = getProduct(row);
      return (
        <>
          {p.name || "-"}{" "}
          {p.weightDetails &&
            `${p.weightDetails.netContent} ${p.weightDetails.unitOfMeasure}`}
        </>
      );
    },
    getSortValue: (row) => getProduct(row).name || "",
  },
  {
    key: "subBrand",
    label: "מותג",
    width: "1.2fr",
    base: true,
    getValue: (row) => getProduct(row).subBrand?.name || "-",
    getSortValue: (row) => getProduct(row).subBrand?.name || "",
  },
  {
    key: "brand",
    label: "ספק",
    width: "1.2fr",
    base: true,
    getValue: (row) => getProduct(row).brand?.name || "-",
    getSortValue: (row) => getProduct(row).brand?.name || "",
  },
  {
    key: "department",
    label: "מחלקה",
    width: "1.3fr",
    base: true,
    getValue: (row) => getCategoryValue(row, 1),
    getSortValue: (row) => getCategoryValue(row, 1),
  },
  {
    key: "category",
    label: "קבוצה",
    width: "1.3fr",
    base: true,
    getValue: (row) => getCategoryValue(row, 2),
    getSortValue: (row) => getCategoryValue(row, 2),
  },
  {
    key: "subCategory",
    label: "תת-קבוצה",
    width: "1.3fr",
    base: true,
    getValue: (row) => getCategoryValue(row, 3),
    getSortValue: (row) => getCategoryValue(row, 3),
  },
  {
    key: "price",
    label: "מחיר",
    width: "0.9fr",
    base: true,
    getValue: (row) => (row.pricing?.price ? `₪${row.pricing.price}` : "-"),
    getSortValue: (row) => row.pricing?.price ?? 0,
  },
  {
    key: "stock",
    label: "מלאי זמין",
    width: "1fr",
    base: true,
    isStockColumn: true,
    getSortValue: (row) => row.availability?.availableQuantity ?? -1,
  },
  {
    key: "status",
    label: "סטטוס",
    width: "0.9fr",
    base: true,
    getValue: (row) => {
      const status = row.categories?.[0]?.productStatus;
      return status === "ACTIVE"
        ? "פעיל"
        : status === "INACTIVE"
          ? "לא פעיל"
          : "-";
    },
    getSortValue: (row) => row.categories?.[0]?.productStatus || "",
  },
  {
    key: "family",
    label: "משפחה",
    width: "0.7fr",
    base: true,
    getValue: (row) => (getFamilyName(row) ? "✔" : "✘"),
    getSortValue: (row) => getFamilyName(row) ?? "\uFFFF",
  },

  ...EXSTRA_COLUMNS,
];

const ProductsTable = ({ products, onSelect }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const extraColumns = useSelector(
    (state) => state.productFilters.extraColumns,
  );

  const visibleColumns = useMemo(
    () =>
      ALL_COLUMNS.filter((col) => col.base || extraColumns.includes(col.key)),
    [extraColumns],
  );

  const gridTemplateColumns = visibleColumns
    .map((col) => col.width || "1fr")
    .join(" ");

  const sortedProducts = useMemo(() => {
    if (!sortKey) return products;

    const column = visibleColumns.find((col) => col.key === sortKey);

    if (!column) return products;

    return [...products].sort((a, b) => {
      const aVal = column.getSortValue(a);
      const bVal = column.getSortValue(b);

      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal), "he");

      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [products, sortKey, sortDir, visibleColumns]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleAvailabilityClick = (e, row) => {
    e.stopPropagation();
    setSelectedProduct(getProduct(row));
    setShowPopup(true);
  };

  return (
    <>
      <div className="products-table">
        <div className="table-header" style={{ gridTemplateColumns }}>
          {visibleColumns.map((col) => (
            <div
              key={col.key}
              className="header-cell"
              onClick={() => handleSort(col.key)}
            >
              {col.label}
              {sortKey === col.key && (
                <span className="sort-arrow">
                  {sortDir === "asc" ? "↑" : "↓"}
                </span>
              )}
            </div>
          ))}
        </div>

        {sortedProducts.map((row, index) => (
          <div
            key={index}
            className="product-row"
            style={{ gridTemplateColumns }}
            onClick={() => onSelect(getProduct(row))}
          >
            {visibleColumns.map((col) => {
              if (col.isStockColumn) {
                const qty = row.availability?.availableQuantity;
                const hasStock = qty > 0;

                return (
                  <div
                    key={col.key}
                    className={`table-cell availability-cell ${
                      hasStock ? "in-stock" : "out-of-stock"
                    }`}
                    onClick={
                      hasStock
                        ? (e) => handleAvailabilityClick(e, row)
                        : undefined
                    }
                  >
                    {hasStock ? (
                      <>
                        <span className="stock-dot" />
                        <span>{qty}</span>
                        <span className="stock-icon">📦</span>
                      </>
                    ) : (
                      <>
                        <span className="stock-dot out" />
                        <span>{qty ?? "-"}</span>
                      </>
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={col.key}
                  className={`table-cell ${col.className || ""}`}
                >
                  {col.getValue(row)}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showPopup && selectedProduct && (
        <PopUp width="900px" close={() => setShowPopup(false)}>
          <h3 className="popup-header">{selectedProduct.name}</h3>
          <ProductLocationPopUp
            product={selectedProduct}
            onClose={() => setShowPopup(false)}
          />
        </PopUp>
      )}
    </>
  );
};

export default ProductsTable;
